import { useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import Toggle from 'components/Toggle';
import { Heading, Text } from 'components/typography';
import useVideoPlayerStore from 'components/video-player/store';
import { SubtitlesContext, SubtitlesContextType } from './contexts';
import SubtitlesHeaderProps from './subtitlesHeader.props';

const SubtitlesHeader = ({ languagesLength }: SubtitlesHeaderProps) => {
  const {
    subtitle,
    handleSubtitle,
    previouslySelectedSubtitle,
    subtitlePreference
  } = useContext(SubtitlesContext) as SubtitlesContextType;

  const { audio, languages } = useVideoPlayerStore(state => ({
    audio: state.audio,
    languages: state.languages
  }));

  // Check if subtitles are currently active based on preference
  const isToggleOn = subtitlePreference.isEnabled;

  const handleToggle = useCallback(() => {
    if (languagesLength === 0) return;

    if (!subtitlePreference.isEnabled) {
      // When turning on, try to use:
      // 1. Previous selected subtitle if it's available
      // 2. Stored preference language if available
      // 3. English if video is muted and English is available
      // 4. First available language
      const availableLanguages =
        languages?.map(lang => lang.languageCode) || [];
      let nextSubtitle = 'OFF';

      if (
        previouslySelectedSubtitle &&
        availableLanguages.includes(previouslySelectedSubtitle)
      ) {
        nextSubtitle = previouslySelectedSubtitle;
      } else if (
        subtitlePreference.language &&
        availableLanguages.includes(subtitlePreference.language)
      ) {
        nextSubtitle = subtitlePreference.language;
      } else if (audio.mute && availableLanguages.includes('en')) {
        nextSubtitle = 'en';
      } else if (availableLanguages.length > 0) {
        nextSubtitle = availableLanguages[0];
      }

      if (nextSubtitle !== 'OFF') {
        handleSubtitle(nextSubtitle);
      }
    } else {
      // Turn off subtitles
      handleSubtitle('OFF');
    }
  }, [
    languagesLength,
    subtitle,
    handleSubtitle,
    previouslySelectedSubtitle,
    subtitlePreference,
    audio.mute,
    languages
  ]);

  return (
    <div
      data-testid="subtitles-header"
      className="flex flex-row justify-between bg-white px-4 py-3 pl-6 sm:mr-3 md:w-full"
    >
      {/* Subtitles header */}
      <div className="flex flex-row items-baseline">
        <Heading size="s">Subtitles &nbsp;</Heading>
        {languagesLength > 0 && (
          <Text size="m">
            <FormattedMessage
              data-testid="subtitles-header-quantity"
              defaultMessage="({langCount} {langCount, plural,
            one {language}
            other {languages}
          })"
              values={{ langCount: languagesLength }}
            />
          </Text>
        )}
      </div>

      {/* On/Off Toggle */}
      <div className="flex flex-row items-center gap-1 whitespace-nowrap">
        <Toggle
          toggledColorDot="bg-white"
          isOn={isToggleOn}
          handleToggle={handleToggle}
          disabled={languagesLength === 0}
        />
        <Text size="m" className="font-medium">
          {isToggleOn ? 'On' : 'Off'}
        </Text>
      </div>
    </div>
  );
};

export default SubtitlesHeader;
